import { DatePipe } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { MatOption } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { CotecnaUserService } from "@cotecna/session";
import { StorageService } from "src/app/session/services/storage.service";
import { PresetsService } from "src/app/settings/services/presets.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  APPROVED_TIME_MANAGEMENT,
  COORDINATOR_READ_ONLY_ROLE,
  INSPECTION_ORIGINS_KEY,
  InspectionTimeManagementState,
  INSPECTOR_ROLE,
  PENDING_TIME_MANAGEMENT,
  RETURNED_TIME_MANAGEMENT,
  SERVICE_PROVIDER_COORDINATOR_ROLE,
  SERVICE_PROVIDER_INSPECTOR_ROLE,
  SUBMITTED_TIME_MANAGEMENT,
} from "../../constants/constants";
import { AdditionalFiltersDialogComponent } from "../../dialogs/additional-filters-dialog/additional-filters-dialog.component";
import {
  CLOSED,
  FINISHED,
  NEW,
  OPEN,
  ORIGIN_BUTTON,
  PARTIALLY_FINISHED,
  STATUS_BUTTON,
  TIMESHEET_STATUS_BUTTON,
  TYPE_BUTTON,
} from "../../model/filters-constants";
import { InspectionState } from "../../model/inspection-state";
import { GeneralInfoService } from "../../services/general-info.service";
import { InspectionFilterService } from "../../services/inspection-filter.service";
import { FilterForm } from "./filter-form-interface";

enum InspectionListState {
  Active = "active",
  Closed = "closed",
}

@Component({
  selector: "im-filters",
  templateUrl: "./filters.component.html",
  styleUrls: ["./filters.component.scss"],
  standalone: false,
})
export class FiltersComponent implements OnInit, OnChanges {
  public constructor(
    private presetsService: PresetsService,
    private datePipe: DatePipe,
    private generalInfoService: GeneralInfoService,
    private cotecnaUserService: CotecnaUserService,
    private inspectionFilterService: InspectionFilterService,
    private dialog: MatDialog,
    private storageService: StorageService,
    private readonly userRolePermissionsService: UserRolePermissionsService,
  ) {}

  @ViewChild("statusSelect") public statusSelect: MatSelect;
  @ViewChild("timesheetStatusSelect") public timesheetStatusSelect: MatSelect;
  @ViewChild("originSelect") public originSelect: MatSelect;
  @ViewChild("typeSelect") public typeSelect: MatSelect;

  @Input() public hideOriginFilter: boolean = false;
  @Input() public isStateActive: boolean = true;
  @Input() public disableSearchButton: boolean;
  @Input() public activeOrClosedInspections: InspectionListState;
  @Output() public searchButton: EventEmitter<FilterForm> =
    new EventEmitter<FilterForm>();
  @Output() public clearButton = new EventEmitter();

  private DATE_FORMAT: string = "yyyy-MM-dd";
  public canStatusFilterBeRemoved: boolean = false;

  public filterFormToSend: FilterForm = {
    status: "",
    origin: "",
    type: "",
    inspectionId: "",
    client: "",
    inspectionReference: "",
    dateFrom: "",
    dateTo: "",
    creationDateFrom: "",
    creationDateTo: "",
    closeDate: "",
    vesselName: "",
    timeManagementState: "",
  };

  public statusValues: string[] = [];
  public originValues: string[] = [];
  public typeValues: any[] = [];
  public timesheetStatusValues: InspectionTimeManagementState[] = [
    InspectionTimeManagementState.Pending,
    InspectionTimeManagementState.Submitted,
    InspectionTimeManagementState.Approved,
    InspectionTimeManagementState.Returned,
  ];

  public inspectionIdField: string = "";
  public clientField: string = "";
  public inspectionReferenceField: string = "";
  public dateTo: any = "";
  public dateFrom: any = "";
  public creationDateTo: any = "";
  public creationDateFrom: any = "";
  public closeDate: any = "";
  public status: any = "";
  public origin: any = "";
  public type: any = "";
  public vesselNameFilter: string = "";
  public timesheetStatus: string = "";

  public isAccountTypeManagementOnly: boolean = false;
  public isInspectionsOnlyAccount: boolean = false;

  public ngOnInit(): void {
    this.getAccountType();
    this.filterFormToSend = this.inspectionFilterService.getFilterForm();
    this.fillFilters();
    this.getTypeValues();
    this.getStatusValues();
    this.getOrigins();
  }

  public ngOnChanges(): void {
    this.getStatusValues();
  }

  private async getAccountType(): Promise<void> {
    this.isAccountTypeManagementOnly =
      this.userRolePermissionsService.isTimeManagementOnlyAccount();
    this.isInspectionsOnlyAccount =
      this.userRolePermissionsService.isInspectionsOnlyAccount();
  }

  private async getOrigins(): Promise<void> {
    const originsRaw = await this.storageService.get(INSPECTION_ORIGINS_KEY);
    if (originsRaw) {
      this.originValues = JSON.parse(JSON.stringify(originsRaw));
    }

    if (this.originValues.length === 0) {
      this.originValues = await this.inspectionFilterService.getOrigins();
      await this.storageService.set(INSPECTION_ORIGINS_KEY, this.originValues);
    }
  }

  private fillFilters(): void {
    this.inspectionIdField = this.filterFormToSend.inspectionId;
    this.clientField = this.filterFormToSend.client;
    this.inspectionReferenceField = this.filterFormToSend.inspectionReference;
    this.dateTo = this.filterFormToSend.dateTo;
    this.dateFrom = this.filterFormToSend.dateFrom;
    this.creationDateTo = this.filterFormToSend.creationDateTo;
    this.creationDateFrom = this.filterFormToSend.creationDateFrom;
    this.closeDate = this.filterFormToSend.closeDate;
    this.status = InspectionState.getInspectionState(
      this.filterFormToSend.status,
    );
    this.statusSelect = this.filterFormToSend.status;
    this.origin = this.filterFormToSend.origin;
    this.type = this.filterFormToSend.type;
    this.vesselNameFilter = this.filterFormToSend.vesselName;
    this.timesheetStatus = this.filterFormToSend.timeManagementState;
    this.timesheetStatusSelect = this.filterFormToSend.timeManagementState;
  }

  private async getTypeValues(): Promise<void> {
    if (!this.isInspectorOrCordinatorReadOnly()) {
      const temp = await this.presetsService.getPresetsList();
      temp.forEach((t) => this.typeValues.push({ name: t.name, id: t.id }));
    }
  }

  private getStatusValues(): void {
    if (this.activeOrClosedInspections == InspectionListState.Active) {
      this.statusValues = [NEW, OPEN, PARTIALLY_FINISHED, FINISHED];

      const usersRoles = this.cotecnaUserService.getUser().roles;
      if (
        usersRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE) ||
        usersRoles.includes(COORDINATOR_READ_ONLY_ROLE)
      ) {
        this.statusValues = [OPEN, PARTIALLY_FINISHED, FINISHED];
      }

      if (this.isOnlyInspector()) {
        this.statusValues = [OPEN, PARTIALLY_FINISHED];
      }

      if (
        this.filterFormToSend.status === InspectionState.Closed ||
        this.filterFormToSend.status === ""
      ) {
        this.getStatusSelection("");
      } else {
        this.getStatusSelection(this.statusSelect);
      }
      this.clearCloseDateField();
    } else {
      if (this.isOnlyInspector()) {
        this.statusValues = [CLOSED, FINISHED];
      } else {
        this.statusValues = [CLOSED];
      }

      if (this.filterFormToSend.status != InspectionState.Closed) {
        this.getStatusSelection("");
      } else {
        this.getStatusSelection(this.statusSelect);
      }
    }
  }

  public isInspectorOrCordinatorReadOnly(): boolean {
    const roles = this.cotecnaUserService.getUser().roles;
    return (
      (roles.length === 1 &&
        (roles[0] === INSPECTOR_ROLE ||
          roles[0] === SERVICE_PROVIDER_INSPECTOR_ROLE)) ||
      roles[0] === COORDINATOR_READ_ONLY_ROLE
    );
  }

  public isOnlyInspector(): boolean {
    const roles = this.cotecnaUserService.getUser().roles;
    return (
      roles.length === 1 &&
      (roles[0] === INSPECTOR_ROLE ||
        roles[0] === SERVICE_PROVIDER_INSPECTOR_ROLE)
    );
  }

  public onClearFilters(): void {
    this.clearInspectionIdField();
    this.filterFormToSend.inspectionId = "";
    this.clearClientField();
    this.filterFormToSend.client = "";
    this.clearInspectionReferenceField();
    this.filterFormToSend.inspectionReference = "";
    this.canStatusFilterBeRemoved = false;
    this.clearDropDownField(this.originSelect);
    this.filterFormToSend.origin = "";
    this.clearInspectionDateField();
    this.filterFormToSend.dateTo = "";
    this.filterFormToSend.dateFrom = "";
    this.clearCreationDateField();
    this.filterFormToSend.creationDateTo = "";
    this.filterFormToSend.creationDateFrom = "";
    this.clearCloseDateField();
    this.filterFormToSend.closeDate = "";
    this.clearDropDownField(this.typeSelect);
    this.filterFormToSend.type = "";
    this.filterFormToSend.status = "";
    this.clearDropDownField(this.statusSelect);
    this.clearVesselNameField();
    this.filterFormToSend.vesselName = "";
    this.filterFormToSend.timeManagementState = "";
    this.clearDropDownField(this.timesheetStatusSelect);
    this.clearButton.emit();
  }

  public onSearchClick(): void {
    this.filterFormToSend.inspectionId = this.inspectionIdField;
    this.filterFormToSend.client = this.clientField;
    this.filterFormToSend.inspectionReference = this.inspectionReferenceField;

    this.filterFormToSend.dateTo = this.dateTo
      ? this.datePipe.transform(this.dateTo, this.DATE_FORMAT)
      : "";
    this.filterFormToSend.dateFrom = this.dateFrom
      ? this.datePipe.transform(this.dateFrom, this.DATE_FORMAT)
      : "";

    this.filterFormToSend.creationDateTo = this.creationDateTo
      ? this.datePipe.transform(this.creationDateTo, this.DATE_FORMAT)
      : "";
    this.filterFormToSend.creationDateFrom = this.creationDateFrom
      ? this.datePipe.transform(this.creationDateFrom, this.DATE_FORMAT)
      : "";

    this.filterFormToSend.closeDate = this.closeDate
      ? this.datePipe.transform(this.closeDate, this.DATE_FORMAT)
      : "";

    this.filterFormToSend.vesselName = this.vesselNameFilter;

    this.searchButton.emit(this.filterFormToSend);
    this.inspectionFilterService.setFilterForm(this.filterFormToSend);
  }

  public getStatusSelection(event: any): void {
    this.canStatusFilterBeRemoved = true;
    switch (event.value) {
      case NEW: {
        this.filterFormToSend.status = InspectionState.New;
        break;
      }
      case OPEN: {
        this.filterFormToSend.status = InspectionState.Open;
        break;
      }
      case PARTIALLY_FINISHED: {
        this.filterFormToSend.status = InspectionState.PartiallyFinished;
        break;
      }
      case FINISHED: {
        this.filterFormToSend.status = InspectionState.Finished;
        break;
      }
      case CLOSED: {
        this.filterFormToSend.status = InspectionState.Closed;
        break;
      }
      default: {
        this.canStatusFilterBeRemoved = false;
        this.clearDropDownField(this.statusSelect);
        break;
      }
    }
  }

  public getOriginSelection(event: any): void {
    this.filterFormToSend.origin = event.value;
  }

  public getTypeSelection(event: any): void {
    this.filterFormToSend.type = event.value;
  }

  public clearInspectionIdField(): void {
    this.inspectionIdField = "";
  }

  public clearClientField(): void {
    this.clientField = "";
  }

  public clearInspectionReferenceField(): void {
    this.inspectionReferenceField = "";
  }

  public clearInspectionDateField(): void {
    this.dateTo = "";
    this.dateFrom = "";
  }
  public clearDropDownField(matSelect: MatSelect): void {
    matSelect?.options?.forEach((data: MatOption) => data.deselect());
  }

  public clearCreationDateField(): void {
    this.creationDateTo = "";
    this.creationDateFrom = "";
    this.filterFormToSend.creationDateTo = "";
    this.filterFormToSend.creationDateFrom = "";
  }

  public clearCloseDateField(): void {
    this.closeDate = "";
    this.filterFormToSend.closeDate = "";
  }

  public clearVesselNameField(): void {
    this.vesselNameFilter = "";
  }

  public clearDropDownFieldEvent(event: Event): void {
    event.stopPropagation();
    switch ((event.currentTarget as any).id) {
      case STATUS_BUTTON: {
        this.clearDropDownField(this.statusSelect);
        this.filterFormToSend.status = "";
        this.canStatusFilterBeRemoved = false;
        break;
      }
      case ORIGIN_BUTTON: {
        this.clearDropDownField(this.originSelect);
        this.filterFormToSend.origin = "";
        break;
      }
      case TYPE_BUTTON: {
        this.clearDropDownField(this.typeSelect);
        this.filterFormToSend.type = "";
        break;
      }
      case TIMESHEET_STATUS_BUTTON: {
        this.clearDropDownField(this.timesheetStatusSelect);
        this.filterFormToSend.timeManagementState = "";
        break;
      }
      default: {
        break;
      }
    }
  }

  public ValidateKeyCode(event: any) {
    this.generalInfoService.ValidateKey(event);
  }

  public async openAdditionalFiltersDialog(): Promise<void> {
    const dialogRef = this.dialog.open(AdditionalFiltersDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: this.getDialogData(),
    });

    const dialogResult = await dialogRef.afterClosed().toPromise();
    if (dialogResult) {
      this.inspectionReferenceField = dialogResult.reference;
      this.type = dialogResult.type;
      this.filterFormToSend.type = dialogResult.type;
      this.origin = dialogResult.origin;
      this.filterFormToSend.origin = dialogResult.origin;
      this.creationDateTo = dialogResult.creationDateTo;
      this.creationDateFrom = dialogResult.creationDateFrom;
      this.closeDate = dialogResult.closeDate;
      this.vesselNameFilter = dialogResult.vesselName;
    }
  }

  private getDialogData(): any {
    return {
      referenceFilter: {
        visible: true,
        value: this.inspectionReferenceField,
      },
      typeFilter: {
        visible: !this.isOnlyInspector(),
        value: this.type,
        options: this.typeValues,
      },
      originFilter: {
        visible: true,
        value: this.origin,
        options: this.originValues,
      },
      creationDateFromFilter: {
        visible: true,
        value: this.creationDateFrom,
      },
      creationDateToFilter: {
        visible: true,
        value: this.creationDateTo,
      },
      vesselNameFilter: {
        visible: true,
        value: this.vesselNameFilter,
      },
      closeDateFilter: {
        visible: !this.isStateActive,
        value: this.closeDate,
      },
    };
  }

  public getTimesheetStatusLabel(value: InspectionTimeManagementState): string {
    switch (value) {
      case InspectionTimeManagementState.Pending:
        return PENDING_TIME_MANAGEMENT;
      case InspectionTimeManagementState.Submitted:
        return SUBMITTED_TIME_MANAGEMENT;
      case InspectionTimeManagementState.Approved:
        return APPROVED_TIME_MANAGEMENT;
      case InspectionTimeManagementState.Returned:
        return RETURNED_TIME_MANAGEMENT;
    }
  }

  public getTimesheetStatusSelection(event: any): void {
    switch (event.value) {
      case InspectionTimeManagementState.Pending: {
        this.filterFormToSend.timeManagementState =
          InspectionTimeManagementState.Pending;
        break;
      }
      case InspectionTimeManagementState.Submitted: {
        this.filterFormToSend.timeManagementState =
          InspectionTimeManagementState.Submitted;
        break;
      }
      case InspectionTimeManagementState.Approved: {
        this.filterFormToSend.timeManagementState =
          InspectionTimeManagementState.Approved;
        break;
      }
      case InspectionTimeManagementState.Returned: {
        this.filterFormToSend.timeManagementState =
          InspectionTimeManagementState.Returned;
        break;
      }
    }
  }
}
